import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  GetDashboardUsersByGender,
  getExportIncompleteProfiles,
  GetServerInfo,
  GetCommunityStatistics,
  GetExportUsersV2,
  GetInstructionExport,
  GetUsersWithInstructions,
  GetUserLikeDislikePairs,
  GetCustomerSupportAdmins,
} from "./apis/get";
import { PostAddPdfSale, PostProcessCallingPdfs } from "./apis/post";
import { ApiRequest } from "../services/api";
import { PatchAssignUserInstruction } from "./apis/patch";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import ConfirmationModal from "./ConfirmationModal";
import {
  BarChart3,
  User,
  FileText,
  Clock,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Heart,
  Zap,
  Filter,
  Users,
  Search,
  UserCheck,
  ChevronDown,
  X,
  Phone,
  History,
  PlusCircle,
} from "lucide-react";

interface CardData {
  title: string;
  subtitle: string;
  icon: string;
}

interface ServerInfo {
  server: {
    platform: string;
    platformVersion: string;
    architecture: string;
    hostname: string;
    pythonVersion: string;
  };
  database: {
    status: string;
    version: string;
    type: string;
  };
  environment: {
    mode: string;
    region: string;
    timezone: string;
  };
  resources: {
    memoryTotal: string;
    memoryUsed: string;
    memoryPercent: string;
    diskTotal: string;
    diskUsed: string;
    diskPercent: string;
    cpuCount: number;
  };
}

interface CommunityStat {
  community: string;
  total_users: number;
  male_count: number;
  female_count: number;
  complete_profiles: number;
  incomplete_profiles: number;
  active_users: number;
  inactive_users: number;
}

interface UserInstruction {
  user_id: string;
  username: string;
  name?: string;
  instruction: string;
  date: string;
  dislike_reason?: string;
  dislike_instruction?: string;
  assigned_to?: string | null;
}

interface EmptyInstructionProfile {
  user_id: string;
  name: string;
  instruction?: string;
  dislike_reason?: string;
  dislike_instruction?: string;
  assigned_to?: string | null;
  system_instruction?: string;
}

interface AwaitingFollowupProfile {
  user_id: string;
  username: string;
  name: string;
  phone_number: number;
  assigned_to: string;
  awiting_follw_up_instruction: string;
  follow_up_date: string;
  follow_up_time: string;
  follow_up_created_at: string;
  type: string;
}

const isSystemOnlyInstruction = (item: any) => {
  const instruction = String(item?.instruction || "").trim().toLowerCase();
  const source = String(item?.source || item?.assigned_to_source || "").trim().toLowerCase();

  if (!instruction || instruction === "n/a" || instruction === "test") return true;
  if (source === "verified_user_auto_assignment") return true;
  if (instruction.includes("auto-assigned")) return true;
  if (/^this user is assigned to .+ at .+/.test(instruction)) return true;
  return false;
};

interface UserInfo {
  matrimonial_id: string;
  user_id: string;
  name: string;
  liked: boolean;
  disliked: boolean;
}

interface UserPair {
  users: [UserInfo, UserInfo];
  latest_action_time: string | null;
}

const TABS = [
  { id: "personal", label: "Personal Details" },
  { id: "matches", label: "Matches" },
  { id: "assigned_unregistered", label: "Assigned / Unregistered" },
  { id: "instructional", label: "Instructional Reports" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [cards, setCards] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [unregisteredCount, setUnregisteredCount] = useState(0);
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null);

  // New: Community Stats
  const [communityStats, setCommunityStats] = useState<CommunityStat[]>([]);
  const [selectedCommunity, setSelectedCommunity] =
    useState<CommunityStat | null>(null);
  const [communityFilter, setCommunityFilter] = useState<string>("");

  // Users with Instructions
  const [usersWithInstructions, setUsersWithInstructions] = useState<
    UserInstruction[]
  >([]);
  const [instructionsCount, setInstructionsCount] = useState(0);
  const [instructionsPage, setInstructionsPage] = useState(1);
  const [instructionLimit, setInstructionLimit] = useState(10);
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [supportAdmins, setSupportAdmins] = useState<any[]>([]);
  const [isAssigning, setIsAssigning] = useState<string | null>(null);
  const [instructionFilter, setInstructionFilter] = useState<string | null>(
    null,
  );
  const [emptyInstructionProfiles, setEmptyInstructionProfiles] = useState<
    EmptyInstructionProfile[]
  >([]);
  const [emptyInstructionCount, setEmptyInstructionCount] = useState(0);
  const [awaitingFollowupProfiles, setAwaitingFollowupProfiles] = useState<
    AwaitingFollowupProfile[]
  >([]);
  const [awaitingFollowupCount, setAwaitingFollowupCount] = useState(0);
  const [activeInstructionTab, setActiveInstructionTab] = useState<
    "has" | "empty" | "awaiting_followup"
  >("has");

  // Engagement Pairs
  const [engagementPairs, setEngagementPairs] = useState<UserPair[]>([]);
  const [engagementCount, setEngagementCount] = useState(0);
  const [pairsFilter, setPairsFilter] = useState<string>("both_liked");
  const [loadingPairs, setLoadingPairs] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [isAssignAllModalOpen, setIsAssignAllModalOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  useEffect(() => {
    const data = localStorage.getItem("data");
    if (data) {
      try {
        const parsed = JSON.parse(data);
        setCurrentUserRole(parsed.Role || null);
        setCurrentUsername(parsed.username || parsed.Username || null);
      } catch (error) {
        console.error("Failed to parse user role", error);
      }
    }
  }, []);

  // Advanced Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [activeExportTab, setActiveExportTab] = useState("personal");
  const [exportFilters, setExportFilters] = useState({
    from_date: "",
    to_date: "",
    is_active: false,
    onboarding_status: "all", // all, complete, incomplete
    has_transactions: false,
    gender: "",
    marital_status: "",
    manglik_status: "",
    match_status: "", // all, published, unpublished
    instruction_subject: "all" as "instruction" | "empty" | "followup" | "all",
    instruction_admin: "all",
    assigned_to: "all",
    columns: [] as string[],
  });

  const toggleColumn = (col: string) => {
    setExportFilters((prev) => {
      const exists = prev.columns.includes(col);
      return {
        ...prev,
        columns: exists
          ? prev.columns.filter((c) => c !== col)
          : [...prev.columns, col],
      };
    });
  };

  const toggleGroup = (groupOptions: string[]) => {
    setExportFilters((prev) => {
      const allSelected = groupOptions.every((opt) =>
        prev.columns.includes(opt),
      );
      let newColumns = [...prev.columns];

      if (allSelected) {
        newColumns = newColumns.filter((c) => !groupOptions.includes(c));
      } else {
        groupOptions.forEach((opt) => {
          if (!newColumns.includes(opt)) newColumns.push(opt);
        });
      }
      return { ...prev, columns: newColumns };
    });
  };

  const fetchDashboardDataByGender = useCallback(async () => {
    setLoading(true);
    setInstructionsPage(1);
    try {
      const [response, serverResponse, communityResponse] = await Promise.all([
        GetDashboardUsersByGender(communityFilter),
        GetServerInfo(),
        GetCommunityStatistics(),
      ]);

      if (response.status === "success") {
        const {
          totalUsers,
          maleCount,
          femaleCount,
          incompleteProfiles,
          completeProfiles,
          unverifiedProfiles,
          premiumUsers,
        } = response.data;

        const updatedCards: CardData[] = [
          {
            title: (totalUsers || 0).toString(),
            subtitle: "Total Users",
            icon: "https://img.icons8.com/?size=100&id=102261&format=png&color=ffffff",
          },
          {
            title: (femaleCount || 0).toString(),
            subtitle: "Female Users",
            icon: "https://img.icons8.com/?size=100&id=7696&format=png&color=ffffff",
          },
          {
            title: (maleCount || 0).toString(),
            subtitle: "Male Users",
            icon: "https://img.icons8.com/?size=100&id=7702&format=png&color=ffffff",
          },
          {
            title: (incompleteProfiles || 0).toString(),
            subtitle: "Incomplete Profiles",
            icon: "https://img.icons8.com/?size=100&id=OtSeG7KvrKjE&format=png&color=ffffff",
          },
          {
            title: (completeProfiles || 0).toString(),
            subtitle: "Complete Profiles",
            icon: "https://img.icons8.com/?size=100&id=102261&format=png&color=ffffff",
          },
          {
            title: (unverifiedProfiles || 0).toString(),
            subtitle: "Unverified Profiles",
            icon: "https://img.icons8.com/?size=100&id=102261&format=png&color=ffffff",
          },
          {
            title: (premiumUsers || 0).toString(),
            subtitle: "Premium Users",
            icon: "https://img.icons8.com/?size=100&id=dFBLFssmbdWE&format=png&color=ffffff",
          },
        ];
        setCards(updatedCards);
      }

      if (response.data?.incompleteProfiles !== undefined) {
        setUnregisteredCount(response.data.incompleteProfiles);
      }

      if (serverResponse && serverResponse.status === "success") {
        setServerInfo(serverResponse.data);
      }

      if (communityResponse && communityResponse.status === "success") {
        setCommunityStats(communityResponse.data);
        if (communityFilter) {
          const matchedCommunity = communityResponse.data.find(
            (item: CommunityStat) => item.community === communityFilter,
          );
          setSelectedCommunity(matchedCommunity || null);
        } else if (communityResponse.data.length > 0) {
          setSelectedCommunity((prev) => prev || communityResponse.data[0]);
        }
      }

      fetchEngagementPairs();
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoading(false);
    }
  }, [communityFilter]);

  const fetchEngagementPairs = useCallback(async () => {
    setLoadingPairs(true);
    try {
      const response = await GetUserLikeDislikePairs({
        filter_type: pairsFilter,
      });

      if (response) {
        let pairsData: UserPair[] = [];
        let totalCount = 0;

        if (response.pairs) {
          if (Array.isArray(response.pairs.pairs)) {
            pairsData = response.pairs.pairs;
            totalCount = response.pairs.count || pairsData.length;
          } else if (Array.isArray(response.pairs)) {
            pairsData = response.pairs;
            totalCount = response.count || response.pairs.length;
          }
        } else if (Array.isArray(response)) {
          pairsData = response;
          totalCount = response.length;
        }

        setEngagementPairs(pairsData);
        setEngagementCount(totalCount);
      } else {
        setEngagementPairs([]);
        setEngagementCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch engagement pairs", error);
      setEngagementPairs([]);
      setEngagementCount(0);
    } finally {
      setLoadingPairs(false);
    }
  }, [pairsFilter]);

  const handleRightClickCopy = (e: React.MouseEvent, text: string) => {
    e.preventDefault();
    navigator.clipboard.writeText(text);
    toast.success(`Copied ID: ${text}`, {
      style: {
        borderRadius: "10px",
        background: "#0f172a",
        color: "#10b981",
        border: "1px solid #10b981",
        fontSize: "12px",
        fontWeight: "bold",
      },
    });
    console.log(`Copied ID: ${text}`);
  };

  useEffect(() => {
    fetchEngagementPairs();
  }, [fetchEngagementPairs]);

  const handleInstructionPageChange = async (
    newPage: number,
    autoAssign = false,
    filterOverride?: string | null,
    limitOverride?: number,
  ) => {
    if (loadingInstructions || newPage < 1) return;

    setLoadingInstructions(true);
    try {
      const limit = limitOverride !== undefined ? limitOverride : instructionLimit;
      const offset = (newPage - 1) * limit;
      const currentFilter =
        filterOverride !== undefined ? filterOverride : instructionFilter;
      const response = await GetUsersWithInstructions(
        limit,
        offset,
        currentFilter as any,
        autoAssign,
      );
      if (response && response.status === "success") {
        const sortedData = (response.data || []).sort(
          (a: any, b: any) =>
            new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        const systemOnlyInstructionProfiles = sortedData.filter(isSystemOnlyInstruction);
        const filteredData = sortedData.filter(
          (item: any) => !isSystemOnlyInstruction(item),
        );
        const movedEmptyProfiles = systemOnlyInstructionProfiles.map((item: any) => ({
          user_id: item.user_id,
          name: item.name || item.username,
          dislike_instruction: item.dislike_instruction,
          dislike_reason: item.dislike_reason,
          assigned_to: item.assigned_to,
          system_instruction: item.instruction,
        }));
        const emptyProfiles = [
          ...movedEmptyProfiles,
          ...(response.empty_instruction_profiles || []),
        ];
        setUsersWithInstructions(filteredData);
        setInstructionsPage(newPage);
        setInstructionsCount(
          Math.max((response.count || sortedData.length) - systemOnlyInstructionProfiles.length, 0),
        );
        setEmptyInstructionProfiles(emptyProfiles);
        setEmptyInstructionCount(
          (response.empty_instruction_profiles_count || 0) + systemOnlyInstructionProfiles.length,
        );
        setAwaitingFollowupProfiles(
          response.awiting_followup_instruction_profile || [],
        );
        setAwaitingFollowupCount(
          response.awiting_followup_instruction_profile_count || 0,
        );
      }
    } catch (error) {
      console.error("Failed to change instructions page", error);
    } finally {
      setLoadingInstructions(false);
    }
  };

  const handleInstructionLimitChange = async (newLimit: number) => {
    setInstructionLimit(newLimit);
    setInstructionsPage(1);
    await handleInstructionPageChange(1, false, undefined, newLimit);
  };

  const handleAssignAll = async () => {
    if (loadingInstructions) return;
    setIsAssignAllModalOpen(true);
  };

  const confirmAssignAll = async () => {
    setIsAssignAllModalOpen(false);
    await handleInstructionPageChange(1, true);
    toast.success("Successfully assigned all instructions");
  };

  const handleCleanup = async (
    type:
      | "full"
      | "same-gender"
      | "self-matches"
      | "inactive-stale"
      | "one-sided-matches",
  ) => {
    if (cleanupLoading) return;

    setCleanupLoading(true);
    try {
      let endpoint = "";
      let params: any = {};

      switch (type) {
        case "full":
          endpoint = "admin/match-cleanup/run";
          params = { inactive_days: 15 };
          break;
        case "same-gender":
          endpoint = "admin/match-cleanup/same-gender";
          break;
        case "self-matches":
          endpoint = "admin/match-cleanup/self-matches";
          break;
        case "inactive-stale":
          endpoint = "admin/match-cleanup/inactive-stale";
          params = { days: 15 };
          break;
        case "one-sided-matches":
          endpoint = "admin/match-cleanup/one-sided-matches";
          break;
      }

      const response = await ApiRequest("POST", endpoint, params as any);

      if (response.status === true || response.status === "success") {
        const deleted = response.deleted || {};
        const totalDeleted =
          response.total_deleted ||
          (deleted.matched_profiles || 0) +
          (deleted.match_making_queue || 0) +
          (deleted.skipped_matches || 0) +
          (deleted.requested_matches || 0);

        toast.success(
          `Cleanup completed! Deleted: ${totalDeleted} records\n` +
          `Profiles: ${deleted.matched_profiles || 0}, Queue: ${deleted.match_making_queue || 0}, ` +
          `Skipped: ${deleted.skipped_matches || 0}, Requested: ${deleted.requested_matches || 0}`,
          { duration: 6000 },
        );
      } else {
        toast.error(response.message || "Cleanup failed");
      }
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error("Cleanup operation failed");
    } finally {
      setCleanupLoading(false);
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const handleExportIncompleteProfiles = async () => {
    setExportLoading(true);
    try {
      const result = await getExportIncompleteProfiles();

      if (result.success) {
        console.log(`Downloaded: ${result.fileName}`);
      } else if (result.message === "No matches found") {
        alert("No incomplete profiles found.");
      } else {
        console.error("Failed to export incomplete profiles", result.error);
        alert("Failed to export incomplete profiles. Please try again.");
      }
    } catch (error) {
      console.error("Error exporting incomplete profiles:", error);
      alert("Error exporting incomplete profiles. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const handlePredefinedExport = async (
    type: "instruction2024" | "empty2025" | "all2025",
  ) => {
    setExportLoading(true);
    try {
      let params: any = {};
      if (type === "instruction2024") {
        params = {
          include_instruction_users: "true",
          start_date: "2024-01-01",
          end_date: "2024-12-31",
        };
      } else if (type === "empty2025") {
        params = {
          include_empty_instruction_users: "true",
          start_date: "2025-02-01",
          end_date: "2025-02-28",
        };
      } else if (type === "all2025") {
        params = {
          include_instruction_users: "true",
          include_empty_instruction_users: "true",
          start_date: "2025-01-01",
          end_date: "2025-03-31",
        };
      }

      const result = await GetExportUsersV2(params);
      if (result) {
        console.log("Predefined export triggered");
      }
      setShowExportModal(false);
    } catch (error) {
      console.error("Predefined export failed", error);
      alert("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleAdvancedExport = async () => {
    setExportLoading(true);
    try {
      const params: any = {};
      if (exportFilters.from_date) params.start_date = exportFilters.from_date;
      if (exportFilters.to_date) params.end_date = exportFilters.to_date;
      if (exportFilters.is_active) params.is_active = "true";
      if (exportFilters.has_transactions) params.has_transactions = "true";

      if (exportFilters.onboarding_status === "complete")
        params.onboarding_complete = "true";
      if (exportFilters.onboarding_status === "incomplete")
        params.onboarding_complete = "false";

      if (exportFilters.gender) params.gender = exportFilters.gender;
      if (exportFilters.marital_status)
        params.marital_status = exportFilters.marital_status;
      if (exportFilters.manglik_status)
        params.manglik_status = exportFilters.manglik_status;
      if (exportFilters.match_status)
        params.match_status = exportFilters.match_status;

      if (activeExportTab === "instructional") {
        const instructionParams: any = {
          from_date: exportFilters.from_date,
          to_date: exportFilters.to_date,
          flag: exportFilters.instruction_subject,
          assigned_to:
            exportFilters.instruction_admin === "all"
              ? ""
              : exportFilters.instruction_admin,
          assigned_to_userid: "",
        };
        const result = await GetInstructionExport(instructionParams);
        if (result) {
          console.log("Instructional Export triggered");
        }
        setShowExportModal(false);
        setExportLoading(false);
        return;
      } else if (activeExportTab === "assigned_unregistered") {
        params.columns = "assigned_completed_unregistered_users";
        if (exportFilters.assigned_to !== "all") {
          params.assigned_to = exportFilters.assigned_to;
        }
      } else if (exportFilters.columns.length > 0) {
        params.columns = exportFilters.columns.join(",");
      }

      const result = await GetExportUsersV2(params);
      if (result) {
        // ApiDownloadRequest handles the download trigger usually
        console.log("Export triggered");
      }
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed", error);
      alert("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const totalUsers = parseInt(cards[0]?.title || "0");
  const maleCount = parseInt(cards[2]?.title || "0");
  const femaleCount = parseInt(cards[1]?.title || "0");
  const incompleteProfiles = parseInt(cards[3]?.title || "0");
  const completeProfiles = parseInt(cards[4]?.title || "0");
  const unverifiedProfiles = parseInt(cards[5]?.title || "0");
  const premiumUsers = parseInt(cards[6]?.title || "0");

  const malePercentage =
    totalUsers > 0 ? Math.round((maleCount / totalUsers) * 100) : 0;
  const femalePercentage =
    totalUsers > 0 ? Math.round((femaleCount / totalUsers) * 100) : 0;

  const fetchSupportAdmins = useCallback(async () => {
    try {
      const response = await GetCustomerSupportAdmins();
      if (Array.isArray(response)) {
        setSupportAdmins(response);
      } else if (response && response.data) {
        setSupportAdmins(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch support admins", error);
    }
  }, []);

  const handleAssignUser = async (userId: string, adminUsername: string) => {
    try {
      const response = await PatchAssignUserInstruction(userId, adminUsername);
      if (response.status === true || response.success === true) {
        toast.success(response.message || "User assigned successfully");
        setIsAssigning(null);
        // Refresh the instructions list
        handleInstructionPageChange(instructionsPage);
      } else {
        toast.error(response.message || "Assignment failed");
      }
    } catch (error) {
      console.error("Error assigning user", error);
      toast.error("An error occurred during assignment");
    }
  };

  useEffect(() => {
    fetchDashboardDataByGender();
    fetchSupportAdmins();
  }, [fetchDashboardDataByGender, fetchSupportAdmins]);

  useEffect(() => {
    handleInstructionPageChange(1);
  }, [instructionFilter]);

  const activeCount =
    activeInstructionTab === "has"
      ? instructionsCount
      : activeInstructionTab === "empty"
        ? emptyInstructionCount
        : awaitingFollowupCount;

  const totalPages = Math.ceil(activeCount / instructionLimit) || 1;

  return (
    <div className="min-h-screen bg-transparent p-4 md:p-8 space-y-8 animate-fade-in font-sans selection:bg-blue-500/30">
      {/* GLOBAL SYSTEM HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-800 pb-10">
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.6)]"></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-black text-blue-500/80">
              System Live • Verified Node
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tighter uppercase italic">
            Official Dashboard
          </h1>
          <p className="text-slate-400 font-medium max-w-xl">
            Deep analytics and infrastructure synchronization for the
            matrimonial network database.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Cleanup Dropdown */}
          <div className="relative group">
            <button
              className="h-11 flex items-center gap-2 bg-orange-600 border border-orange-500 text-white text-xs font-black px-6 rounded-lg hover:bg-orange-500 transition shadow-lg shadow-orange-500/20 uppercase tracking-widest"
              disabled={cleanupLoading}
            >
              <span>🧹</span>
              {cleanupLoading ? "Processing..." : "Cleanup"}
              <ChevronDown
                size={14}
                className="group-hover:rotate-180 transition-transform"
              />
            </button>
            <div className="absolute top-full left-0 mt-2 w-64 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => handleCleanup("full")}
                  disabled={cleanupLoading}
                  className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-red-400">🔥</span>
                  <div>
                    <div className="font-black uppercase tracking-widest">
                      Full Cleanup
                    </div>
                    <div className="text-[10px] text-slate-400 font-medium">
                      Remove all stale matches (15 days)
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleCleanup("same-gender")}
                  disabled={cleanupLoading}
                  className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-yellow-400">⚠️</span>
                  <div>
                    <div className="font-black uppercase tracking-widest">
                      Same Gender
                    </div>
                    <div className="text-[10px] text-slate-400 font-medium">
                      Remove same-gender matches
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleCleanup("self-matches")}
                  disabled={cleanupLoading}
                  className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-blue-400">🔄</span>
                  <div>
                    <div className="font-black uppercase tracking-widest">
                      Self Matches
                    </div>
                    <div className="text-[10px] text-slate-400 font-medium">
                      Remove self-referencing matches
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleCleanup("inactive-stale")}
                  disabled={cleanupLoading}
                  className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-purple-400">💤</span>
                  <div>
                    <div className="font-black uppercase tracking-widest">
                      Inactive Stale
                    </div>
                    <div className="text-[10px] text-slate-400 font-medium">
                      Remove inactive user matches (15 days)
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleCleanup("one-sided-matches")}
                  disabled={cleanupLoading}
                  className="w-full text-left px-4 py-3 text-xs font-bold text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-orange-400">⚡</span>
                  <div>
                    <div className="font-black uppercase tracking-widest">
                      One-Sided Matches
                    </div>
                    <div className="text-[10px] text-slate-400 font-medium">
                      Remove matches without reverse pair
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowExportModal(true)}
            className="h-11 flex items-center gap-2 bg-blue-600 border border-blue-500 text-white text-xs font-black px-6 rounded-lg hover:bg-blue-500 transition shadow-lg shadow-blue-500/20 uppercase tracking-widest"
          >
            Export
          </button>

          <select
            value={communityFilter}
            onChange={(e) => setCommunityFilter(e.target.value)}
            className="h-11 min-w-[220px] bg-slate-950 border border-slate-800 text-white text-xs font-bold rounded-lg px-4 focus:outline-none focus:ring-1 focus:ring-blue-500 transition uppercase tracking-widest"
          >
            <option value="">All Communities</option>
            {communityStats.map((community) => (
              <option key={community.community} value={community.community}>
                {community.community}
              </option>
            ))}
          </select>

          <button
            onClick={fetchDashboardDataByGender}
            className="h-11 px-6 bg-slate-900 border border-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-800 transition shadow-sm uppercase tracking-widest"
            disabled={loading}
          >
            {loading ? "SYNCING..." : "Refresh Feed"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT COLUMN: Intelligence Cluster (8 columns) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Top Level Intelligence Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {loading ? (
              <>
                <SkeletonStat />
                <SkeletonStat />
                <SkeletonStat />
              </>
            ) : (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <StatBox
                    title="Complete Profiles"
                    value={completeProfiles.toLocaleString()}
                    desc="Fully Onboarded Members"
                    color="text-blue-500"
                    icon="🌐"
                    onClick={() => navigate("/users?status=complete")}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <StatBox
                    title="Incomplete Profiles"
                    value={incompleteProfiles.toLocaleString()}
                    desc="Profiles Requiring Action"
                    color="text-orange-500"
                    icon="⚠️"
                    onClick={() => navigate("/users?status=incomplete")}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <StatBox
                    title="Unverified Profiles"
                    value={unverifiedProfiles.toLocaleString()}
                    desc="Pending Verification"
                    color="text-yellow-500"
                    icon="🔒"
                    onClick={() => navigate("/users?status=unverified")}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <StatBox
                    title="Premium Users"
                    value={premiumUsers.toLocaleString()}
                    desc="Active Subscriptions"
                    color="text-emerald-500"
                    icon="💎"
                    onClick={() => navigate("/users?premium=true")}
                  />
                </motion.div>
              </>
            )}
          </div>

          {/* Visual Data Monitor (Charts) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gender Distribution Bar Chart */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-900/30 border border-slate-800 rounded-3xl p-8 relative overflow-hidden group h-[380px] flex flex-col"
            >
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none select-none">
                <div className="text-[12rem] font-black leading-none text-white uppercase italic">
                  GEN
                </div>
              </div>
              <div className="relative z-10 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.3em] mb-1">
                      Gender Distribution
                    </h3>
                  </div>
                </div>

                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: "Male", count: maleCount, color: "#3b82f6" },
                        {
                          name: "Female",
                          count: femaleCount,
                          color: "#f472b6",
                        },
                      ]}
                      margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#1e293b"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        stroke="#94a3b8"
                        fontSize={10}
                        fontWeight="900"
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={10}
                        fontWeight="900"
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.05)" }}
                        content={<CustomTooltip />}
                      />
                      <Bar
                        dataKey="count"
                        radius={[8, 8, 0, 0]}
                        barSize={50}
                        animationDuration={1500}
                      >
                        {[
                          { name: "Male", count: maleCount },
                          { name: "Female", count: femaleCount },
                        ].map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index === 0 ? "#3b82f6" : "#ec4899"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </motion.div>

            {/* Registration Status Pie Chart */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-900/30 border border-slate-800 rounded-3xl p-8 relative overflow-hidden group h-[380px] flex flex-col"
            >
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none select-none">
                <div className="text-[12rem] font-black leading-none text-white uppercase italic">
                  REG
                </div>
              </div>
              <div className="relative z-10 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.3em] mb-1">
                      User Registration
                    </h3>
                  </div>
                </div>

                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Onboarded", value: totalUsers },
                          { name: "Unregistered", value: unregisteredCount },
                        ]}
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={8}
                        dataKey="value"
                        animationBegin={200}
                        animationDuration={1800}
                      >
                        <Cell fill="#3b82f6" stroke="none" />
                        <Cell fill="#f59e0b" stroke="none" />
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => (
                          <span className="text-[10px] font-black uppercase text-white tracking-widest">
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Unified Instructions Monitor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-900/30 border border-slate-800 rounded-2xl overflow-hidden group"
          >
            <div className="px-6 py-4 border-b border-slate-800/50 bg-slate-950/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <FileText size={16} className="text-blue-400" />
                  </div>
                  <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em]">
                    Instruction Manager
                  </h3>
                </div>

                {/* Tab Switcher */}
                <div className="flex p-1 bg-slate-950/50 rounded-xl border border-slate-800">
                  <button
                    onClick={() => {
                      setActiveInstructionTab("has");
                      handleInstructionPageChange(1);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${activeInstructionTab === "has" ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    With Notes ({instructionsCount})
                  </button>
                  <button
                    onClick={() => {
                      setActiveInstructionTab("empty");
                      handleInstructionPageChange(1);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${activeInstructionTab === "empty" ? "bg-yellow-600 text-white shadow-lg shadow-yellow-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    Empty ({emptyInstructionCount})
                  </button>
                  <button
                    onClick={() => {
                      setActiveInstructionTab("awaiting_followup");
                      handleInstructionPageChange(1);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${activeInstructionTab === "awaiting_followup" ? "bg-purple-600 text-white shadow-lg shadow-purple-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    Awaiting Followup ({awaitingFollowupCount})
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Assigned To Filter */}
                <div className="flex items-center gap-2 h-full">
                  <Filter size={10} className="text-slate-500" />
                  <select
                    className="bg-transparent text-[9px] font-black text-slate-400 focus:text-white focus:outline-none uppercase cursor-pointer"
                    value={instructionFilter || ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : e.target.value;
                      setInstructionFilter(val);
                      // Trigger fetch
                      setIsAssigning(null);
                      handleInstructionPageChange(1, false, val);
                    }}
                  >
                    <option value="" className="bg-slate-900">
                      All Admins
                    </option>
                    {supportAdmins.map((admin) => (
                      <option
                        key={admin.id}
                        value={admin.username}
                        className="bg-slate-900"
                      >
                        {admin.username}
                      </option>
                    ))}
                  </select>
                  {(currentUserRole === "ADMIN" ||
                    currentUserRole === "SUPER_ADMIN") && (
                      <>
                        <div className="h-4 w-px bg-slate-800 mx-2" />
                        <button
                          onClick={handleAssignAll}
                          disabled={loadingInstructions}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/10 text-blue-400 border border-blue-500/20 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all disabled:opacity-50 active:scale-95 shadow-lg shadow-blue-500/5 group"
                        >
                          <Users
                            size={10}
                            className="group-hover:scale-110 transition-transform"
                          />
                          Assign All
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>

            <div className="divide-y divide-slate-800/50 max-h-[850px] overflow-y-auto custom-scrollbar">
              {loading ? (
                <div key="loading-state">
                  {Array(3)
                    .fill(0)
                    .map((_, i) => (
                      <div
                        key={i}
                        className="p-4 animate-pulse flex items-center gap-4"
                      >
                        <div className="w-8 h-8 bg-slate-800 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <div className="h-2 bg-slate-800 rounded w-1/4" />
                          <div className="h-2 bg-slate-800 rounded w-3/4" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div key={`tab-content-${activeInstructionTab}`}>
                  {activeInstructionTab === "has" ? (
                    usersWithInstructions.length > 0 ? (
                      usersWithInstructions.map((item, idx) => (
                      <div
                          key={`has-${item.user_id}-${item.date}-${idx}`}
                          className="p-4 hover:bg-slate-800/30 transition-all flex flex-col md:flex-row md:items-center justify-between gap-3 group/row cursor-pointer"
                          onClick={() =>
                            navigate(`/users/user/${item.user_id}`, {
                              state: { from: "dashboard" },
                            })
                          }
                          onContextMenu={(e) =>
                            handleRightClickCopy(e, item.user_id)
                          }
                          title="Right-click to copy User ID"
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div className="w-8 h-8 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center text-slate-500 group-hover/row:border-blue-500/50 group-hover/row:text-blue-400 transition-all shrink-0">
                              <User size={16} />
                            </div>
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-black text-white uppercase italic truncate">
                                  {item.name || item.username}
                                </span>
                                <span className="text-[8px] font-mono text-slate-600">
                                  #{item.user_id}
                                </span>
                                {item.assigned_to && (
                                  <span className="text-[7px] font-black text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20 uppercase tracking-widest flex items-center gap-1 shrink-0">
                                    <UserCheck size={8} /> {item.assigned_to}
                                  </span>
                                )}
                              </div>
                              {item.instruction && item.instruction !== "" && (
                                <div className="text-[10px] text-slate-400 font-medium leading-normal italic line-clamp-2">
                                  "{item.instruction}"
                                </div>
                              )}
                              {(item.dislike_instruction ||
                                item.dislike_reason) &&
                                (item.dislike_instruction !== "" ||
                                  item.dislike_reason !== "") && (
                                  <div className="text-[9px] text-rose-400 font-bold leading-normal bg-rose-500/5 px-2 py-1 rounded-md border border-rose-500/10 mt-1 inline-block">
                                    DISLIKE:{" "}
                                    {(
                                      item.dislike_instruction ||
                                      item.dislike_reason ||
                                      ""
                                    ).length > 60
                                      ? `${(item.dislike_instruction || item.dislike_reason || "").substring(0, 60)}...`
                                      : item.dislike_instruction ||
                                      item.dislike_reason}
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="flex flex-row md:flex-col items-center md:items-end gap-3 md:gap-2 shrink-0 pl-11 md:pl-0">
                            <div className="flex items-center gap-2">
                              {currentUserRole === "CUSTOMER_SUPPORT" ? (
                                !item.assigned_to && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (currentUsername)
                                        handleAssignUser(
                                          item.user_id,
                                          currentUsername,
                                        );
                                      else
                                        toast.error(
                                          "Unable to identify current user",
                                        );
                                    }}
                                    className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg transition-all group/assign"
                                  >
                                    <UserCheck
                                      size={10}
                                      className="text-blue-400 group-hover/assign:scale-110 transition-transform"
                                    />
                                    <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest">
                                      Assign to Myself
                                    </span>
                                  </button>
                                )
                              ) : isAssigning === item.user_id ? (
                                <div
                                  className="flex items-center gap-1 bg-slate-950 border border-slate-800 rounded-lg p-1 animate-in slide-in-from-right-2 duration-200"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <select
                                    className="bg-transparent text-[10px] font-black text-blue-400 focus:outline-none cursor-pointer uppercase pr-6"
                                    onChange={(e) =>
                                      handleAssignUser(
                                        item.user_id,
                                        e.target.value,
                                      )
                                    }
                                    defaultValue=""
                                  >
                                    <option value="" disabled>
                                      Select Support
                                    </option>
                                    {supportAdmins.map((admin) => (
                                      <option
                                        key={admin.id}
                                        value={admin.username}
                                        className="bg-slate-900"
                                      >
                                        {admin.username}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => setIsAssigning(null)}
                                    className="p-1 hover:text-rose-500 transition-colors"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsAssigning(item.user_id);
                                  }}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg transition-all group/assign"
                                >
                                  <UserCheck
                                    size={10}
                                    className="text-blue-400 group-hover/assign:scale-110 transition-transform"
                                  />
                                  <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest">
                                    Assign
                                  </span>
                                </button>
                              )}
                            </div>
                            {item.date && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Clock size={8} />
                                <span className="text-[8px] font-bold uppercase tracking-tighter">
                                  {new Date(item.date).toLocaleDateString([], {
                                    day: "2-digit",
                                    month: "short",
                                  })}{" "}
                                  •{" "}
                                  {new Date(item.date).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center space-y-2">
                        <div className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center mx-auto text-slate-700">
                          <FileText size={20} />
                        </div>
                        <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                          No instructions
                        </div>
                      </div>
                    )
                  ) : activeInstructionTab === "awaiting_followup" ? (
                    awaitingFollowupProfiles.length > 0 ? (
                      awaitingFollowupProfiles.map((item, idx) => (
                      <div
                        key={`awaiting-${item.user_id}-${idx}`}
                        className="p-4 hover:bg-slate-800/30 transition-all flex flex-col md:flex-row md:items-start justify-between gap-4 group/row cursor-pointer border-b border-slate-800/50 last:border-0"
                        onClick={() =>
                          navigate(`/users/user/${item.user_id}`, {
                            state: { from: "dashboard" },
                          })
                        }
                        onContextMenu={(e) =>
                          handleRightClickCopy(e, item.user_id)
                        }
                        title="Right-click to copy User ID"
                      >
                          <div className="flex items-start gap-4 flex-1">
                            <div className="w-10 h-10 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center text-slate-500 group-hover/row:border-purple-500/50 group-hover/row:text-purple-400 transition-all shrink-0">
                              <User size={20} />
                            </div>
                            <div className="space-y-2 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-black text-white uppercase italic truncate">
                                  {item.name || item.username}
                                </span>
                                <span className="text-[10px] font-mono text-slate-500 bg-slate-950 px-2 py-0.5 rounded border border-slate-800">
                                  #{item.user_id}
                                </span>
                                {item.assigned_to && (
                                  <span className="text-[8px] font-black text-purple-400 bg-purple-500/10 px-2 py-1 rounded border border-purple-500/20 uppercase tracking-widest flex items-center gap-1.5 shrink-0">
                                    <UserCheck size={10} /> {item.assigned_to}
                                  </span>
                                )}
                                {item.phone_number && (
                                  <span className="text-[9px] font-bold text-blue-400 bg-blue-500/10 px-2 py-1 rounded border border-blue-500/20 flex items-center gap-1.5">
                                    <Phone size={10} /> {item.phone_number}
                                  </span>
                                )}
                              </div>

                              {item.awiting_follw_up_instruction && (
                                <div className="text-[11px] text-slate-300 font-medium leading-relaxed italic bg-slate-950/50 p-2.5 rounded-lg border border-slate-800/50">
                                  <span className="text-purple-500/50 mr-1 font-black">
                                    "
                                  </span>
                                  {item.awiting_follw_up_instruction}
                                  <span className="text-purple-500/50 ml-1 font-black">
                                    "
                                  </span>
                                </div>
                              )}

                              <div className="flex flex-wrap items-center gap-3 mt-1">
                                <div className="flex items-center gap-1.5 text-slate-500">
                                  <PlusCircle size={10} />
                                  <span className="text-[9px] font-bold uppercase tracking-tight">
                                    Created At:{" "}
                                    {formatFullDateTime(
                                      item.follow_up_created_at,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-row md:flex-col items-center md:items-end gap-3 md:gap-2 shrink-0 pl-14 md:pl-0">
                            {item.follow_up_date && (
                              <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg shadow-lg shadow-purple-500/5">
                                  <Calendar size={12} />
                                  <span className="text-[10px] font-black uppercase tracking-widest">
                                    Follow-up:{" "}
                                    {formatFullDateTime(
                                      item.follow_up_date +
                                      "T" +
                                      (item.follow_up_time || "00:00:00"),
                                    )}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center space-y-2">
                        <div className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center mx-auto text-slate-700">
                          <Clock size={20} />
                        </div>
                        <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                          No profiles awaiting followup
                        </div>
                      </div>
                    )
                  ) : emptyInstructionProfiles.length > 0 ? (
                    emptyInstructionProfiles.map((item, idx) => (
                    <div
                      key={`empty-${item.user_id}-${idx}`}
                      className="p-4 hover:bg-slate-800/30 transition-all flex items-center justify-between gap-3 group/row cursor-pointer"
                      onClick={() =>
                        navigate(`/users/user/${item.user_id}`, {
                          state: { from: "dashboard" },
                        })
                      }
                      onContextMenu={(e) =>
                        handleRightClickCopy(e, item.user_id)
                      }
                      title="Right-click to copy User ID"
                    >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center text-slate-600 group-hover/row:border-yellow-500/50 group-hover/row:text-yellow-400 transition-all shrink-0">
                            <User size={16} />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-black text-white uppercase italic truncate">
                                {item.name}
                              </span>
                              <span className="text-[8px] font-mono text-slate-600">
                                #{item.user_id}
                              </span>
                              {item.assigned_to && (
                                <span className="text-[7px] font-black text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded border border-yellow-500/20 uppercase tracking-widest flex items-center gap-1 shrink-0">
                                  <UserCheck size={8} /> {item.assigned_to}
                                </span>
                              )}
                            </div>
                            {(item.system_instruction || item.instruction) && (
                              <div className="text-[10px] text-slate-400 font-medium leading-normal italic line-clamp-2 mt-1">
                                "{item.system_instruction || item.instruction}"
                              </div>
                            )}
                            {(item.dislike_instruction ||
                              item.dislike_reason) &&
                              (item.dislike_instruction !== "" ||
                                item.dislike_reason !== "") && (
                                <div className="text-[9px] text-rose-400 font-bold leading-normal bg-rose-500/5 px-2 py-1 rounded-md border border-rose-500/10 mt-1 self-start">
                                  DISLIKE:{" "}
                                  {(
                                    item.dislike_instruction ||
                                    item.dislike_reason ||
                                    ""
                                  ).length > 60
                                    ? `${(item.dislike_instruction || item.dislike_reason || "").substring(0, 60)}...`
                                    : item.dislike_instruction ||
                                    item.dislike_reason}
                                </div>
                              )}
                          </div>
                          {/* Assignment removed as requested */}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center space-y-2">
                      <div className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center mx-auto text-slate-700">
                        <FileText size={20} />
                      </div>
                      <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                        All profiles have instructions
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pagination Controls */}
              {!loading &&
                ((activeInstructionTab === "has" &&
                  (usersWithInstructions.length > 0 ||
                    instructionsPage > 1 ||
                    instructionsCount > instructionLimit)) ||
                  (activeInstructionTab === "empty" &&
                    (emptyInstructionProfiles.length > 0 ||
                      instructionsPage > 1 ||
                      emptyInstructionCount > instructionLimit)) ||
                  (activeInstructionTab === "awaiting_followup" &&
                    (awaitingFollowupProfiles.length > 0 ||
                      instructionsPage > 1 ||
                      awaitingFollowupCount > instructionLimit))) && (
                  <div className="px-6 py-4 border-t border-slate-800 bg-slate-950 flex flex-col sm:flex-row items-center justify-between gap-4 sticky bottom-0 z-10">
                    <div className="flex items-center gap-4">
                      <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                        Page {instructionsPage}{" "}
                        <span className="text-slate-700">/</span>{" "}
                        {totalPages}
                      </div>
                      {loadingInstructions && (
                        <div className="w-3 h-3 border-2 border-blue-500/50 border-t-blue-500 rounded-full animate-spin" />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-6">
                      {/* Limit / Page Size Selector */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Show:</span>
                        <select
                          value={instructionLimit}
                          onChange={(e) => handleInstructionLimitChange(Number(e.target.value))}
                          className="bg-slate-900 border border-slate-800 rounded-md px-2.5 py-1 text-[10px] font-bold text-slate-400 hover:text-white hover:border-slate-700 focus:text-white focus:outline-none focus:border-blue-500/50 transition-all uppercase cursor-pointer"
                        >
                          <option value={5} className="bg-slate-900">5</option>
                          <option value={10} className="bg-slate-900">10</option>
                          <option value={15} className="bg-slate-900">15</option>
                          <option value={20} className="bg-slate-900">20</option>
                        </select>
                      </div>

                      {/* Direct Page Switcher Dropdown */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Go to:</span>
                        <select
                          value={instructionsPage}
                          onChange={(e) => handleInstructionPageChange(Number(e.target.value))}
                          className="bg-slate-900 border border-slate-800 rounded-md px-2.5 py-1 text-[10px] font-bold text-slate-400 hover:text-white hover:border-slate-700 focus:text-white focus:outline-none focus:border-blue-500/50 transition-all uppercase cursor-pointer"
                        >
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <option key={p} value={p} className="bg-slate-900">
                              {p}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Arrow Buttons */}
                      <div className="flex gap-1.5">
                        <button
                          onClick={() =>
                            handleInstructionPageChange(instructionsPage - 1)
                          }
                          disabled={loadingInstructions || instructionsPage === 1}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed group/prev"
                        >
                          <ChevronLeft
                            size={14}
                            className="group-hover/prev:-translate-x-0.5 transition-transform"
                          />
                        </button>
                        <button
                          onClick={() =>
                            handleInstructionPageChange(instructionsPage + 1)
                          }
                          disabled={
                            loadingInstructions ||
                            instructionsPage >= totalPages
                          }
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed group/next"
                        >
                          <ChevronRight
                            size={14}
                            className="group-hover/next:translate-x-0.5 transition-transform"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </motion.div>


          {/* Engagement Pairs Monitor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-900/30 border border-slate-800 rounded-2xl overflow-hidden group"
          >
            <div className="px-6 py-4 border-b border-slate-800/50 bg-slate-950/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-pink-500/10 flex items-center justify-center border border-pink-500/20">
                    <Heart size={16} className="text-pink-400" />
                  </div>
                  <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em]">
                    Engagement Pairs
                  </h3>
                </div>

                {/* Filter Switcher */}
                <div className="flex p-1 bg-slate-950/50 rounded-xl border border-slate-800">
                  <button
                    onClick={() => setPairsFilter("both_liked")}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${pairsFilter === "both_liked" ? "bg-pink-600 text-white shadow-lg shadow-pink-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    Both Liked
                  </button>
                  <button
                    onClick={() => setPairsFilter("both_disliked")}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${pairsFilter === "both_disliked" ? "bg-rose-600 text-white shadow-lg shadow-rose-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    Both Disliked
                  </button>
                  <button
                    onClick={() => setPairsFilter("one_liked_one_disliked")}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${pairsFilter === "one_liked_one_disliked" ? "bg-amber-600 text-white shadow-lg shadow-amber-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    Conflict
                  </button>
                  <button
                    onClick={() => setPairsFilter("")}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${pairsFilter === "" ? "bg-slate-600 text-white shadow-lg shadow-slate-600/20" : "text-slate-500 hover:text-slate-300"}`}
                  >
                    All
                  </button>
                </div>
              </div>

              <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                {engagementCount} Pairs Detected
              </div>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto custom-scrollbar">
              {loadingPairs ? (
                Array(4)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="h-24 bg-slate-800/20 animate-pulse rounded-2xl border border-slate-800"
                    />
                  ))
              ) : engagementPairs.length > 0 ? (
                engagementPairs.map((pair, idx) => (
                  <div
                    key={`${pair.users[0].user_id}-${pair.users[1].user_id}-${idx}`}
                    className="p-4 bg-slate-950/40 border border-slate-800 rounded-2xl flex flex-col gap-3 hover:border-pink-500/30 transition-all group/pair relative overflow-hidden"
                  >
                    <div className="flex items-center justify-between relative z-10">
                      {/* User 1 */}
                      <div
                        className="flex flex-col cursor-pointer hover:text-blue-400 transition-colors"
                        onClick={() =>
                          navigate(`/users/user/${pair.users[0].user_id}`, {
                            state: { from: "dashboard" },
                          })
                        }
                        onContextMenu={(e) =>
                          handleRightClickCopy(e, pair.users[0].user_id)
                        }
                        title="Right-click to copy User ID"
                      >
                        <span className="text-[10px] font-black text-white uppercase tracking-tighter">
                          {pair.users[0].name || pair.users[0].user_id}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[8px] font-mono text-slate-600">
                            ID: {pair.users[0].matrimonial_id}
                          </span>
                        </div>
                        <div className="flex gap-1 mt-1">
                          {pair.users[0].liked && (
                            <span className="text-[7px] text-emerald-500 bg-emerald-500/10 px-1 rounded uppercase font-black">
                              Liked
                            </span>
                          )}
                          {pair.users[0].disliked && (
                            <span className="text-[7px] text-rose-500 bg-rose-500/10 px-1 rounded uppercase font-black">
                              Disliked
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1">
                          <div className="h-px w-6 bg-slate-800"></div>
                          {pair.users[0].liked && pair.users[1].liked ? (
                            <Heart
                              size={14}
                              className="text-pink-500 fill-pink-500"
                            />
                          ) : pair.users[0].disliked &&
                            pair.users[1].disliked ? (
                            <Zap
                              size={14}
                              className="text-rose-500 fill-rose-500"
                            />
                          ) : (pair.users[0].liked && pair.users[1].disliked) ||
                            (pair.users[0].disliked && pair.users[1].liked) ? (
                            <Filter size={14} className="text-amber-500" />
                          ) : (
                            <Clock size={14} className="text-slate-700" />
                          )}
                          <div className="h-px w-6 bg-slate-800"></div>
                        </div>
                        <div className="text-[7px] font-black text-slate-700 uppercase tracking-widest mt-0.5">
                          {pair.users[0].liked && pair.users[1].liked
                            ? "Mutual"
                            : pair.users[0].disliked && pair.users[1].disliked
                              ? "Avoid"
                              : "Action"}
                        </div>
                      </div>

                      {/* User 2 */}
                      <div
                        className="flex flex-col items-end cursor-pointer hover:text-blue-400 transition-colors"
                        onClick={() =>
                          navigate(`/users/user/${pair.users[1].user_id}`, {
                            state: { from: "dashboard" },
                          })
                        }
                        onContextMenu={(e) =>
                          handleRightClickCopy(e, pair.users[1].user_id)
                        }
                        title="Right-click to copy User ID"
                      >
                        <span className="text-[10px] font-black text-white uppercase tracking-tighter text-right">
                          {pair.users[1].name || pair.users[1].user_id}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[8px] font-mono text-slate-600">
                            ID: {pair.users[1].matrimonial_id}
                          </span>
                        </div>
                        <div className="flex gap-1 mt-1">
                          {pair.users[1].liked && (
                            <span className="text-[7px] text-emerald-500 bg-emerald-500/10 px-1 rounded uppercase font-black">
                              Liked
                            </span>
                          )}
                          {pair.users[1].disliked && (
                            <span className="text-[7px] text-rose-500 bg-rose-500/10 px-1 rounded uppercase font-black">
                              Disliked
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center items-center text-[8px] font-black uppercase tracking-[0.2em] pt-2 border-t border-slate-800/30">
                      {pair.latest_action_time && (
                        <div className="flex items-center gap-1 text-slate-600 font-mono italic lowercase tracking-normal">
                          <Clock size={8} />
                          {pair.latest_action_time.split("T")[0]}{" "}
                          {pair.latest_action_time
                            .split("T")[1]
                            ?.split(".")[0] || ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-12 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center mx-auto text-slate-800">
                    <Users size={20} />
                  </div>
                  <div className="text-[9px] font-black text-slate-700 uppercase tracking-widest">
                    No pairs found in cluster
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* RIGHT COLUMN: Infrastructure & Integrity (4 columns) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Technical Health Stack */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden">
            <div className="p-8 border-b border-slate-800">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">
                Gender Breakdown
              </h3>
            </div>

            <div className="p-8 space-y-8">
              {/* Male Distribution */}
              <div className="space-y-3">
                <div className="flex justify-between items-end">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                    Male Percentage
                  </span>
                  <span className="text-lg font-black text-blue-500">
                    {malePercentage}%
                  </span>
                </div>
                <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden border border-slate-800">
                  <div
                    className="bg-blue-500 h-full rounded-full transition-all duration-1000"
                    style={{ width: `${malePercentage}%` }}
                  ></div>
                </div>
              </div>

              {/* Female Distribution */}
              <div className="space-y-3">
                <div className="flex justify-between items-end">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                    Female Percentage
                  </span>
                  <span className="text-lg font-black text-pink-500">
                    {femalePercentage}%
                  </span>
                </div>
                <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden border border-slate-800">
                  <div
                    className="bg-pink-500 h-full rounded-full transition-all duration-1000"
                    style={{ width: `${femalePercentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-800 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500">
                    Storage Cluster
                  </span>
                  <span className="text-[10px] font-black text-blue-400 uppercase tracking-tighter bg-blue-400/10 px-2 py-0.5 rounded border border-blue-400/20">
                    Operational
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500">
                    Service Queues
                  </span>
                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-tighter bg-emerald-400/10 px-2 py-0.5 rounded border border-emerald-400/20">
                    Synced
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Server Telemetry Monitor */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 relative group overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
              <div className="text-4xl">🛰️</div>
            </div>
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6">
              Resource Telemetry
            </h3>
            <div className="space-y-6">
              {/* CPU & Arch */}
              <div className="flex items-center justify-between p-4 bg-slate-950/60 rounded-2xl border border-slate-800">
                <div>
                  <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">
                    Architecture
                  </div>
                  <div className="text-sm font-bold text-white uppercase italic">
                    {serverInfo?.server.architecture || "AMD64"} /{" "}
                    {serverInfo?.resources.cpuCount || 8} Cores
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">
                    Host
                  </div>
                  <div className="text-[10px] font-bold text-blue-400 uppercase">
                    {serverInfo?.server.hostname || "LOCAL-NODE"}
                  </div>
                </div>
              </div>

              {/* RAM Usage */}
              <div className="space-y-2">
                <div className="flex justify-between items-end">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      Memory Allocation
                    </span>
                    {parseFloat(serverInfo?.resources.memoryPercent || "0") >
                      90 && (
                        <span className="text-[8px] bg-red-500 text-white px-1.5 py-0.5 rounded font-black animate-pulse">
                          CRITICAL
                        </span>
                      )}
                  </div>
                  <span className="text-xs font-black text-white">
                    {serverInfo?.resources.memoryPercent || "0%"}
                  </span>
                </div>
                <div className="w-full h-1 bg-slate-950 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${parseFloat(serverInfo?.resources.memoryPercent || "0") > 90 ? "bg-red-500" : "bg-blue-500"}`}
                    style={{
                      width: serverInfo?.resources.memoryPercent || "0%",
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-[9px] font-bold text-slate-600 uppercase">
                  <span>
                    Used: {serverInfo?.resources.memoryUsed || "0 GB"}
                  </span>
                  <span>
                    Total: {serverInfo?.resources.memoryTotal || "0 GB"}
                  </span>
                </div>
              </div>

              {/* Disk Usage */}
              <div className="space-y-2">
                <div className="flex justify-between items-end">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      Disk Integrity
                    </span>
                    {parseFloat(serverInfo?.resources.diskPercent || "0") >
                      90 && (
                        <span className="text-[8px] bg-red-500 text-white px-1.5 py-0.5 rounded font-black animate-pulse">
                          LOW SPACE
                        </span>
                      )}
                  </div>
                  <span className="text-xs font-black text-white">
                    {serverInfo?.resources.diskPercent || "0%"}
                  </span>
                </div>
                <div className="w-full h-1 bg-slate-950 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${parseFloat(serverInfo?.resources.diskPercent || "0") > 90 ? "bg-red-500" : "bg-emerald-500"}`}
                    style={{ width: serverInfo?.resources.diskPercent || "0%" }}
                  ></div>
                </div>
                <div className="flex justify-between text-[9px] font-bold text-slate-600 uppercase">
                  <span>Used: {serverInfo?.resources.diskUsed || "0 GB"}</span>
                  <span>
                    Capacity: {serverInfo?.resources.diskTotal || "0 GB"}
                  </span>
                </div>
              </div>

              {/* System Hardware & Runtime */}
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-800">
                <div className="p-3 bg-slate-950/40 rounded-xl border border-white/5">
                  <div className="text-[8px] font-bold text-slate-600 uppercase mb-1">
                    OS Platform
                  </div>
                  <div className="text-[10px] font-black text-white uppercase">
                    {serverInfo?.server.platform || "Windows"}
                  </div>
                  <div className="text-[8px] text-slate-500 font-mono mt-0.5">
                    {serverInfo?.server.platformVersion || "v10.0"}
                  </div>
                </div>
                <div className="p-3 bg-slate-950/40 rounded-xl border border-white/5">
                  <div className="text-[8px] font-bold text-slate-600 uppercase mb-1">
                    Runtime
                  </div>
                  <div className="text-[10px] font-black text-blue-400 uppercase">
                    Python {serverInfo?.server.pythonVersion || "3.x"}
                  </div>
                  <div className="text-[8px] text-slate-500 font-mono mt-0.5">
                    Interpreter Active
                  </div>
                </div>
              </div>

              {/* Environment & Metadata */}
              <div className="space-y-3 pt-4 border-t border-slate-800">
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-bold text-slate-500 uppercase">
                    Deployment Mode
                  </span>
                  <span className="text-[9px] font-black text-white bg-blue-500/20 px-2 py-0.5 rounded border border-blue-500/30 uppercase tracking-tighter">
                    {serverInfo?.environment.mode || "Production"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-bold text-slate-500 uppercase">
                    Global Region
                  </span>
                  <span className="text-[9px] font-black text-slate-300 uppercase italic">
                    {serverInfo?.environment.region || "EU-NORTH-1"}
                  </span>
                </div>
                <div className="flex justify-between items-center text-[9px]">
                  <span className="text-slate-500 uppercase font-bold">
                    Timezone
                  </span>
                  <span className="text-slate-400 font-mono">
                    {serverInfo?.environment.timezone || "UTC"}
                  </span>
                </div>
              </div>

              {/* Database Connectivity */}
              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      {serverInfo?.database.type || "PostgreSQL"} Status
                    </span>
                  </div>
                  <span className="text-[10px] font-black text-emerald-500 uppercase font-mono">
                    {serverInfo?.database.status || "CONNECTED"}
                  </span>
                </div>
                <div className="mt-2 p-2 bg-slate-950 rounded border border-white/5">
                  <div className="text-[8px] font-bold text-slate-600 uppercase mb-1">
                    System Version
                  </div>
                  <div
                    className="text-[9px] text-slate-400 font-mono leading-relaxed line-clamp-2"
                    title={serverInfo?.database.version}
                  >
                    {serverInfo?.database.version || "v15.3 Build Package"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Community Analytics Section */}
      <div className="pt-10 border-t border-slate-800">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full shadow-[0_0_8px_rgba(99,102,241,0.6)]"></div>
          <span className="text-[10px] uppercase tracking-[0.3em] font-black text-indigo-400">
            Demographic Distribution
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[500px]">
          {/* Main Community Chart */}
          <div className="lg:col-span-2 bg-slate-900/50 border border-slate-800 rounded-[2.5rem] p-8 relative flex flex-col">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">
                Community Load Factor
              </h3>
              <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-lg border border-white/10 text-[10px] font-bold text-slate-400">
                <BarChart3 size={12} /> Live Distribution
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={communityStats}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="commBarGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8} />
                      <stop
                        offset="100%"
                        stopColor="#6366f1"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#1e293b"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="community"
                    stroke="#475569"
                    fontSize={10}
                    fontWeight="900"
                    tickLine={false}
                    axisLine={false}
                    tick={{ dy: 10 }}
                  />
                  <YAxis
                    stroke="#475569"
                    fontSize={10}
                    fontWeight="900"
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                    content={<CustomTooltip />}
                  />
                  <Bar
                    dataKey="total_users"
                    radius={[8, 8, 0, 0]}
                    fill="url(#commBarGradient)"
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Community List / Gender Split */}
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-0 relative overflow-hidden flex flex-col">
            <div className="p-8 border-b border-white/5 bg-slate-950/30">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                Node Inspector
              </h3>
              <p className="text-[10px] font-bold text-slate-400">
                Select a community to view details
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
              {communityStats.map((s) => (
                <div
                  key={s.community}
                  onClick={() => setSelectedCommunity(s)}
                  className={`p-4 rounded-2xl border transition-all cursor-pointer ${selectedCommunity?.community === s.community ? "bg-indigo-600/20 border-indigo-500/50" : "bg-slate-950/20 border-white/5 hover:bg-slate-900"}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[11px] font-black text-white uppercase">
                      {s.community}
                    </span>
                    {selectedCommunity?.community === s.community && (
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{
                          width: `${(s.male_count / s.total_users) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <span className="text-[9px] font-mono text-slate-500">
                      {((s.male_count / s.total_users) * 100).toFixed(0)}% M
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {selectedCommunity && (
              <div className="p-6 bg-slate-950/80 border-t border-white/5">
                <div className="flex justify-between items-end">
                  <div>
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
                      Selected Node
                    </div>
                    <div className="text-xl font-black text-white">
                      {selectedCommunity.community}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-indigo-400">
                      {selectedCommunity.total_users}
                    </div>
                    <div className="text-[9px] font-bold text-slate-600 uppercase">
                      Total Profiles
                    </div>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `/users?community=${encodeURIComponent(
                        selectedCommunity.community,
                      )}`,
                    )
                  }
                  className="mt-4 w-full rounded-2xl border border-indigo-500/30 bg-indigo-500/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-indigo-300 transition hover:bg-indigo-500/20"
                >
                  View Community Profiles
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="pt-12 border-t border-slate-800 flex justify-between items-center text-[10px] font-black text-slate-600 uppercase tracking-[0.3em]">
        <div>PLATFORM ENGINE v2.0.4</div>
        <div>&copy; SMARTMAHESWARI</div>
      </footer>

      {showExportModal && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-20">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowExportModal(false)}
          />
          <div className="relative bg-slate-900 border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl flex flex-col">
            <div className="p-8 border-b border-white/5 flex justify-between items-center sticky top-0 bg-slate-900 z-10">
              <div>
                <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">
                  Advanced Data Export
                </h2>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">
                  Select filters •{" "}
                  <span className="text-blue-400">
                    Leave empty to download all data
                  </span>
                </p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 hover:bg-slate-700 text-white transition"
              >
                ✕
              </button>
            </div>

            {/* Tab Headers */}
            <div className="flex border-b border-slate-700">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveExportTab(tab.id)}
                  className={`px-6 py-4 text-xs font-black uppercase tracking-widest transition-colors relative ${activeExportTab === tab.id
                      ? "text-white"
                      : "text-slate-500 hover:text-slate-300"
                    }`}
                >
                  {tab.label}
                  {activeExportTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                  )}
                </button>
              ))}
            </div>

            <div className="p-8 space-y-8">
              {/* Personal Details Tab Content */}
              {activeExportTab === "personal" && (
                <>
                  {/* Demographic Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Gender
                      </label>
                      <select
                        value={exportFilters.gender}
                        onChange={(e) =>
                          setExportFilters({
                            ...exportFilters,
                            gender: e.target.value,
                          })
                        }
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                      >
                        <option value="">All Genders</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Marital Status
                      </label>
                      <select
                        value={exportFilters.marital_status}
                        onChange={(e) =>
                          setExportFilters({
                            ...exportFilters,
                            marital_status: e.target.value,
                          })
                        }
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                      >
                        <option value="">All Statuses</option>
                        <option value="unmarried">Unmarried</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                        <option value="awaiting_divorce">
                          Awaiting Divorce
                        </option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Manglik Status
                      </label>
                      <select
                        value={exportFilters.manglik_status}
                        onChange={(e) =>
                          setExportFilters({
                            ...exportFilters,
                            manglik_status: e.target.value,
                          })
                        }
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                      >
                        <option value="">All Types</option>
                        <option value="manglik">Manglik</option>
                        <option value="non_manglik">Non-Manglik</option>
                        <option value="anshik">Anshik</option>
                        <option value="dont_know">Don't Know</option>
                      </select>
                    </div>
                  </div>

                  {/* Status Filters */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-black text-white uppercase tracking-widest border-b border-white/5 pb-2">
                      Status Filters
                    </h3>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-3 bg-slate-950/50 px-4 py-3 rounded-xl border border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={exportFilters.is_active}
                          onChange={(e) =>
                            setExportFilters({
                              ...exportFilters,
                              is_active: e.target.checked,
                            })
                          }
                          className="w-4 h-4 rounded-md border-slate-700 bg-slate-900 text-blue-500 focus:ring-offset-slate-900"
                        />
                        <span className="text-xs font-bold text-slate-300 uppercase">
                          Active Users Only
                        </span>
                      </label>
                      <label className="flex items-center gap-3 bg-slate-950/50 px-4 py-3 rounded-xl border border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={exportFilters.has_transactions}
                          onChange={(e) =>
                            setExportFilters({
                              ...exportFilters,
                              has_transactions: e.target.checked,
                            })
                          }
                          className="w-4 h-4 rounded-md border-slate-700 bg-slate-900 text-blue-500 focus:ring-offset-slate-900"
                        />
                        <span className="text-xs font-bold text-slate-300 uppercase">
                          Has Transactions
                        </span>
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-4 pt-2">
                      <div className="flex items-center gap-2 bg-slate-950 max-w-fit p-1 rounded-xl border border-slate-800">
                        {["all", "complete", "incomplete"].map((status) => (
                          <button
                            key={status}
                            onClick={() =>
                              setExportFilters({
                                ...exportFilters,
                                onboarding_status: status,
                              })
                            }
                            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${exportFilters.onboarding_status === status ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-white"}`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase self-center">
                        Onboarding Status
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Matches Tab Content */}
              {activeExportTab === "matches" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Match Status
                      </label>
                      <select
                        value={exportFilters.match_status}
                        onChange={(e) =>
                          setExportFilters({
                            ...exportFilters,
                            match_status: e.target.value,
                          })
                        }
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                      >
                        <option value="">All Match Statuses</option>
                        <option value="published">Published</option>
                        <option value="unpublished">Unpublished</option>
                      </select>
                    </div>
                  </div>

                  {/* Date Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={exportFilters.from_date}
                        onChange={(e) =>
                          setExportFilters({
                            ...exportFilters,
                            from_date: e.target.value,
                          })
                        }
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={exportFilters.to_date}
                        onChange={(e) =>
                          setExportFilters({
                            ...exportFilters,
                            to_date: e.target.value,
                          })
                        }
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                      />
                    </div>
                  </div>
                </>
              )}

              {activeExportTab === "assigned_unregistered" && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                  <div className="bg-slate-950/50 p-6 rounded-2xl border border-white/5 space-y-6">
                    <div className="space-y-2 max-w-[320px]">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Assigned To
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserCheck
                            size={11}
                            className="text-slate-500 group-focus-within:text-blue-500 transition-colors"
                          />
                        </div>
                        <select
                          value={exportFilters.assigned_to}
                          onChange={(e) =>
                            setExportFilters({
                              ...exportFilters,
                              assigned_to: e.target.value,
                            })
                          }
                          className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-8 pr-7 py-1.5 text-white text-[10px] font-bold focus:outline-none focus:border-blue-500 transition-colors appearance-none cursor-pointer"
                        >
                          <option value="all">All Assigned Users</option>
                          {supportAdmins.map((admin) => (
                            <option
                              key={admin.id || admin.username}
                              value={admin.username}
                            >
                              {admin.username}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <ChevronDown size={11} className="text-slate-500" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          From Date
                        </label>
                        <input
                          type="date"
                          value={exportFilters.from_date}
                          onChange={(e) =>
                            setExportFilters({
                              ...exportFilters,
                              from_date: e.target.value,
                            })
                          }
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          To Date
                        </label>
                        <input
                          type="date"
                          value={exportFilters.to_date}
                          onChange={(e) =>
                            setExportFilters({
                              ...exportFilters,
                              to_date: e.target.value,
                            })
                          }
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-[0.2em]">
                    This export downloads assigned completed unregistered users
                    only. Use the assigned admin filter to narrow results.
                  </div>
                </div>
              )}

              {/* Instructional Reports Tab Content */}
              {activeExportTab === "instructional" && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                  {/* Subject Selection (Selection Button Style) */}
                  <div className="bg-slate-950/50 p-6 rounded-2xl border border-white/5 space-y-6">
                    <div className="flex flex-wrap items-center justify-between gap-6">
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          Report Category
                        </label>
                        <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-xl border border-slate-800 w-fit">
                          {[
                            { id: "instruction", label: "With Instruction" },
                            { id: "empty", label: "Without Instruction" },
                            { id: "followup", label: "Awaiting Followup" },
                            { id: "all", label: "All" },
                          ].map((item) => (
                            <button
                              key={item.id}
                              onClick={() =>
                                setExportFilters({
                                  ...exportFilters,
                                  instruction_subject: item.id as any,
                                })
                              }
                              className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${exportFilters.instruction_subject === item.id ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20" : "text-slate-500 hover:text-white"}`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3 flex-1 max-w-[200px]">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          Admin / Assigned To
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <UserCheck
                              size={11}
                              className="text-slate-500 group-focus-within:text-blue-500 transition-colors"
                            />
                          </div>
                          <select
                            value={exportFilters.instruction_admin}
                            onChange={(e) =>
                              setExportFilters({
                                ...exportFilters,
                                instruction_admin: e.target.value,
                              })
                            }
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-8 pr-7 py-1.5 text-white text-[10px] font-bold focus:outline-none focus:border-blue-500 transition-colors appearance-none cursor-pointer"
                          >
                            <option value="all">All Admins</option>
                            {supportAdmins.map((admin) => (
                              <option
                                key={admin.id || admin.username}
                                value={admin.username}
                              >
                                {admin.username}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <ChevronDown size={11} className="text-slate-500" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          From Date
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Calendar
                              size={11}
                              className="text-slate-500 group-focus-within:text-blue-500 transition-colors"
                            />
                          </div>
                          <input
                            type="date"
                            value={exportFilters.from_date}
                            onChange={(e) =>
                              setExportFilters({
                                ...exportFilters,
                                from_date: e.target.value,
                              })
                            }
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-8 pr-4 py-1.5 text-white text-[10px] font-bold focus:outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          To Date
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Calendar
                              size={11}
                              className="text-slate-500 group-focus-within:text-blue-500 transition-colors"
                            />
                          </div>
                          <input
                            type="date"
                            value={exportFilters.to_date}
                            onChange={(e) =>
                              setExportFilters({
                                ...exportFilters,
                                to_date: e.target.value,
                              })
                            }
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-8 pr-4 py-1.5 text-white text-[10px] font-bold focus:outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Columns */}
              {(activeExportTab === "personal" ||
                activeExportTab === "matches") && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-end border-b border-white/5 pb-2">
                      <h3 className="text-xs font-black text-white uppercase tracking-widest">
                        Column Selection
                      </h3>
                      <button
                        onClick={() =>
                          setExportFilters({ ...exportFilters, columns: [] })
                        }
                        className="text-[10px] font-bold text-red-400 hover:text-red-300 uppercase"
                      >
                        Reset Columns
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      {(activeExportTab === "personal"
                        ? PERSONAL_EXPORT_COLUMNS
                        : MATCH_EXPORT_COLUMNS
                      ).map((group) => (
                        <div key={group.label} className="space-y-3">
                          <div className="flex justify-between items-center">
                            <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                              {group.label}
                            </h4>
                            <button
                              onClick={() => toggleGroup(group.options)}
                              className="text-[9px] font-bold text-slate-500 hover:text-white uppercase transition-colors bg-slate-800/50 px-2 py-1 rounded"
                            >
                              {group.options.every((o) =>
                                exportFilters.columns.includes(o),
                              )
                                ? "Deselect All"
                                : "Select All"}
                            </button>
                          </div>
                          <div className="space-y-2">
                            {group.options.map((col) => (
                              <label
                                key={col}
                                className="flex items-center gap-2 cursor-pointer group/item"
                              >
                                <div
                                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${exportFilters.columns.includes(col) ? "bg-blue-500 border-blue-500" : "bg-slate-950 border-slate-800 group-hover/item:border-slate-600"}`}
                                >
                                  {exportFilters.columns.includes(col) && (
                                    <span className="text-white text-[10px]">
                                      ✓
                                    </span>
                                  )}
                                </div>
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={exportFilters.columns.includes(col)}
                                  onChange={() => toggleColumn(col)}
                                />
                                <span
                                  className={`text-[11px] font-medium uppercase ${exportFilters.columns.includes(col) ? "text-white" : "text-slate-500 group-hover/item:text-slate-300"}`}
                                >
                                  {col.replace(/_/g, " ")}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="p-8 border-t border-white/5 bg-slate-900 sticky bottom-0 z-10 flex justify-end gap-4">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-6 py-3 rounded-xl font-bold text-xs uppercase text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAdvancedExport}
                disabled={
                  exportLoading ||
                  (activeExportTab === "matches" &&
                    (!exportFilters.from_date ||
                      !exportFilters.to_date ||
                      !exportFilters.match_status))
                }
                className={`px-8 py-3 rounded-xl font-black text-xs uppercase text-white transition-all flex items-center gap-2 ${exportLoading ||
                    (activeExportTab === "matches" &&
                      (!exportFilters.from_date ||
                        !exportFilters.to_date ||
                        !exportFilters.match_status))
                    ? "bg-slate-700 cursor-not-allowed opacity-50"
                    : "bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-600/20 active:scale-95"
                  }`}
              >
                {exportLoading ? "Processing..." : "Generate Export"}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={isAssignAllModalOpen}
        onClose={() => setIsAssignAllModalOpen(false)}
        onConfirm={confirmAssignAll}
        title="Confirm Bulk Assignment"
        description="Are you sure you want to automatically distribute all unassigned users to the customer support team? This process cannot be undone."
        variant="green"
      />
    </div>
  );
}

/* Helper Components */
const StatBox = ({
  title,
  value,
  desc,
  color,
  icon,
  onClick,
}: {
  title: string;
  value: string;
  desc: string;
  color: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) => (
  <div
    onClick={onClick}
    className={`bg-slate-900 border border-slate-800 rounded-3xl p-8 group hover:border-slate-700 hover:bg-slate-900/50 transition-all duration-300 ${onClick ? "cursor-pointer active:scale-95" : ""}`}
  >
    <div className="flex justify-between items-start mb-6">
      <div className="w-10 h-10 flex items-center justify-center bg-slate-950 rounded-xl border border-slate-800 text-lg group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <div className="text-[10px] font-black text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity italic">
        Live Update
      </div>
    </div>
    <div className={`text-4xl font-black ${color} tracking-tighter mb-1`}>
      {value}
    </div>
    <div className="text-[10px] font-black text-white uppercase tracking-widest mb-1">
      {title}
    </div>
    <div className="text-[10px] font-bold text-slate-600 uppercase tracking-tighter">
      {desc}
    </div>
  </div>
);

const SkeletonStat = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 animate-pulse">
    <div className="w-10 h-10 bg-slate-950 rounded-xl mb-6 shadow-inner" />
    <div className="h-8 bg-slate-800 rounded w-2/3 mb-2" />
    <div className="h-4 bg-slate-800 rounded w-1/2" />
  </div>
);

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-800 p-2 px-3 rounded-lg shadow-2xl">
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-0.5">
          {payload[0].payload.name || payload[0].name}
        </span>
        <span className="text-lg font-black text-white tracking-tighter">
          {(payload[0].value || 0).toLocaleString()}
        </span>
      </div>
    );
  }
  return null;
};

const PERSONAL_EXPORT_COLUMNS = [
  {
    label: "Core User Information",
    options: [
      "user_id",
      "full_name",
      "email",
      "phone",
      "age",
      "gender",
      "is_active",
      "onboarding_complete",
      "created_at",
      "admin_name",
    ],
  },
  {
    label: "Profile Details",
    options: [
      "marital_status",
      "height_cm",
      "weight_kg",
      "complexion",
      "blood_group",
      "disability_status",
      "religion",
      "caste",
      "sub_caste",
      "gothra",
      "languages_known",
      "manglik_status",
      "date_of_birth",
      "place_of_birth",
      "time_of_birth",
      "highest_education",
      "occupation",
      "annual_income",
      "food_habits",
      "drinking_habits",
      "smoking_habits",
      "hobbies",
      "about_me",
      "managed_by",
      "family_type",
      "father_name",
      "father_occupation",
      "mother_name",
      "mother_occupation",
      "sibling_count",
      "family_residence",
      "native_place",
      "alternate_contact",
    ],
  },
  {
    label: "Partner Preferences",
    options: [
      "pref_age_min",
      "pref_age_max",
      "pref_age_negotiable",
      "pref_marital_status",
      "pref_marital_status_negotiable",
      "pref_family_type",
      "pref_family_type_negotiable",
      "pref_family_background",
      "pref_family_background_negotiable",
      "pref_manglik_status",
      "pref_food_habits",
      "pref_food_habits_negotiable",
      "pref_highest_education",
      "pref_highest_education_negotiable",
      "pref_occupation",
      "pref_occupation_negotiable",
      "pref_working_with",
      "pref_working_with_negotiable",
      "pref_annual_income_min",
      "pref_annual_income_max",
      "pref_annual_income_negotiable",
      "pref_preferred_location",
      "pref_location_type",
      "pref_location_negotiable",
      "pref_relocation_willingness",
      "pref_relocation_negotiable",
      "pref_hobbies",
      "pref_hobbies_negotiable",
      "pref_drinking",
      "pref_drinking_negotiable",
      "pref_smoking",
      "pref_smoking_negotiable",
    ],
  },
  {
    label: "Financial Summary",
    options: [
      "total_transactions",
      "total_amount_spent",
      "total_credits_purchased",
      "last_transaction_date",
    ],
  },
];

const MATCH_EXPORT_COLUMNS = [
  {
    label: "Core Match Data",
    options: [
      "match_id",
      "user_id",
      "user_name",
      "user_phone",
      "candidate_id",
      "candidate_name",
      "candidate_phone",
      "admin_name",
    ],
  },
  {
    label: "Scores & Metrics",
    options: ["match_score", "gunn_score", "proximity"],
  },
  {
    label: "Status & Timing",
    options: ["published", "published_at", "created_at"],
  },
  {
    label: "User Profile Data",
    options: ["user_gender", "user_date_of_birth", "user_city", "user_state"],
  },
];

const formatTo12Hour = (timeStr: string) => {
  if (!timeStr) return "";
  try {
    const [hours, minutes] = timeStr.split(":");
    let h = parseInt(hours);
    const m = minutes;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    return `${h}:${m} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const formatFullDateTime = (dateStr: string) => {
  if (!dateStr) return "";
  try {
    // Split into date and time parts manually to avoid timezone shifting
    const parts = dateStr.split("T");
    const datePart = parts[0];
    const timePart = parts[1] || "";

    if (!datePart) return dateStr;

    const [year, month, day] = datePart.split("-").map(Number);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthName = months[month - 1];

    if (!timePart) {
      return `${day.toString().padStart(2, "0")} ${monthName} ${year}`;
    }

    const timeClean = timePart.split(".")[0]; // remove milliseconds
    const [hours, minutes] = timeClean.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
    const displayMinutes = minutes.toString().padStart(2, "0");

    return `${day.toString().padStart(2, "0")} ${monthName} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
  } catch (e) {
    return dateStr;
  }
};
